import type { IPageAxisValue } from '@atlas/types'
import { CardShell } from '@atlas/ui-components'
import { cn, Skeleton, Spinner } from '@atlas/ui-primitives'
import type { ReactNode } from 'react'
import type { HierarchyChoice } from './product-hierarchy'

const SELECT_CLASS =
	'rounded-ds-sm bg-transparent py-0.5 pr-1 text-sm font-semibold text-ds-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring'
/** The bar's own box, and the caption that names it — shared with its skeleton. */
const BAR_CLASS = 'mb-6 flex flex-wrap items-center gap-3 p-0 px-4 py-3'
const CAPTION_CLASS = 'font-mono text-ds-2xs uppercase tracking-ds-wider text-ds-text-subtle'
/** A level's chip, at the height the real one stands at. */
const CHIP_CLASS =
	'inline-flex items-center gap-2 rounded-ds-full border border-ds-border bg-ds-surface py-1 pl-3 pr-1.5'

interface LevelChipProps {
	label: string
	children: ReactNode
}

/**
 * The shared shell every level of the hierarchy sits in: its name, then its control.
 *
 * Not the `Chip` primitive, and no longer named as if it were: this holds a
 * `<label>` and a `<select>`, where the primitive renders a `<span>` around text.
 */
function LevelChip({ label, children }: LevelChipProps) {
	return (
		<span className={CHIP_CLASS}>
			<label
				htmlFor={`resolving-${label}`}
				className="font-mono text-ds-2xs uppercase tracking-wide text-ds-text-subtle"
			>
				{label}
			</label>
			{children}
		</span>
	)
}

/** The width each level's placeholder chip stands at, roughly as long as its name and value. */
const SKELETON_CHIP_WIDTHS = ['w-32', 'w-32', 'w-28', 'w-32']

/**
 * The bar before the page read lands: its caption, then a placeholder per level.
 *
 * The four level names are the bar's own text, but they are not shown here — a named level
 * with nothing behind it renders its empty hint ("no assets in any region yet"), which is a
 * claim about coverage that no read has answered yet.
 */
export function ResolvingForBarSkeleton() {
	return (
		<CardShell className={BAR_CLASS}>
			<span className={CAPTION_CLASS}>Resolving for</span>
			{SKELETON_CHIP_WIDTHS.map((width, i) => (
				<Skeleton key={i} className={cn('h-8 rounded-ds-full', width)} />
			))}
		</CardShell>
	)
}

interface LevelSelectProps {
	/** The level, named as the chip's label and used for the control's id. */
	label: string
	choices: HierarchyChoice[]
	value: string | null
	onChange: (value: string | null) => void
	/**
	 * The `value=""` option — what this level reads as when it stands at nothing. `Any` on a
	 * level that may be left open; a prompt on one that has to be picked.
	 */
	placeholder: string
	/** Shown in place of the select when this level has nothing to offer. */
	emptyHint: string
}

/**
 * One level of the hierarchy. With nothing to offer the control goes inert and says so
 * rather than falling back to the whole axis: a product cannot be resolved for a market it
 * has no content for, and offering one would imply coverage it does not have.
 */
function LevelSelect({ label, choices, value, onChange, placeholder, emptyHint }: LevelSelectProps) {
	if (choices.length === 0) {
		return (
			<LevelChip label={label}>
				<span className="pr-1.5 text-sm italic text-ds-text-subtle" title={emptyHint}>
					{emptyHint}
				</span>
			</LevelChip>
		)
	}

	return (
		<LevelChip label={label}>
			<select
				id={`resolving-${label}`}
				value={value ?? ''}
				onChange={(e) => onChange(e.target.value || null)}
				className={SELECT_CLASS}
			>
				<option value="">{placeholder}</option>
				{choices.map((choice) => (
					<option key={choice.value} value={choice.value}>
						{choice.label}
					</option>
				))}
			</select>
		</LevelChip>
	)
}

export interface ResolvingForBarProps {
	/** The regions the product reaches — never the whole geography axis. */
	regions: IPageAxisValue[]
	region: string | null
	onRegionChange: (code: string | null) => void
	/** The markets of the picked region. Empty until a region is picked. */
	markets: IPageAxisValue[]
	market: string | null
	onMarketChange: (code: string | null) => void
	/** Every brand in the catalogue, keyed by brand ID. */
	brands: HierarchyChoice[]
	brand: string | null
	onBrandChange: (brandId: string | null) => void
	/** The picked brand's products, keyed by product ID. Picking one opens its page. */
	products: HierarchyChoice[]
	/** `null` while the picked brand is not the open product's — nothing is resolved yet. */
	product: string | null
	onProductChange: (productId: string) => void
	/**
	 * True while the page is still resolving the scope shown here. The controls stay live —
	 * a second change must not wait on the first — only the caption reports the wait.
	 */
	isResolving?: boolean
	className?: string
}

/**
 * The catalogue hierarchy the page resolves against: Region / Market / Brand / Product.
 *
 * Stated explicitly rather than left implicit in the URL, because every live region below
 * (identity, applicable rules, brand kit, delivered assets) is an answer about *this*
 * product in *this* market — a reader who cannot see the question cannot judge the answer.
 * Hence the caption.
 *
 * Only the hierarchy. Everything else that narrows the page is a filter over its content,
 * and lives in the rail beside it — a filter and an identity are different kinds of thing,
 * and one bar holding both made neither legible.
 *
 * Presentational: the container owns the hierarchy, mirrors the geography scope into the
 * URL and navigates when the product changes.
 */
export function ResolvingForBar({
	regions,
	region,
	onRegionChange,
	markets,
	market,
	onMarketChange,
	brands,
	brand,
	onBrandChange,
	products,
	product,
	onProductChange,
	isResolving,
	className,
}: ResolvingForBarProps) {
	return (
		<CardShell className={cn(BAR_CLASS, className)}>
			<span className={CAPTION_CLASS}>Resolving for</span>

			<LevelSelect
				label="region"
				choices={regions.map(({ code, label }) => ({ value: code, label }))}
				value={region}
				onChange={onRegionChange}
				placeholder="Any"
				emptyHint="no assets in any region yet"
			/>
			<LevelSelect
				label="market"
				choices={markets.map(({ code, label }) => ({ value: code, label }))}
				value={market}
				onChange={onMarketChange}
				placeholder="Whole region"
				emptyHint={region ? 'no assets in a single market yet' : 'pick a region first'}
			/>
			<LevelSelect
				label="brand"
				choices={brands}
				value={brand}
				onChange={onBrandChange}
				placeholder="Pick a brand"
				emptyHint="no brands in the catalogue"
			/>
			<LevelSelect
				label="product"
				choices={products}
				value={product}
				// A product is never cleared — the page IS one — so only a pick is reported.
				onChange={(productId) => productId && onProductChange(productId)}
				placeholder="Pick a product"
				emptyHint={brand ? 'no products under this brand' : 'pick a brand first'}
			/>

			{/* The caption is where the wait is reported: the hierarchy below it is already the
			    one asked for, so without it a page of dimmed answers has no stated cause. */}
			<span className="ml-auto flex items-center gap-2 font-mono text-ds-2xs text-ds-text-subtle">
				{isResolving ? (
					<>
						<Spinner size="sm" label="Resolving the hierarchy" className="h-3 w-3" />
						resolving…
					</>
				) : (
					<>live: rules &amp; assets update with the hierarchy</>
				)}
			</span>
		</CardShell>
	)
}
